import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, updateDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  formatDistanceToNowStrict,
  parseISO,
  differenceInMinutes,
} from 'date-fns';
import notificationSound from '../assets/order-alert.mp3';
import { FaMobile, FaSoundcloud, FaSpeakerDeck, FaVolumeOff, FaVolumeUp } from 'react-icons/fa';
import { MdOutlineVolumeMute, MdOutlineVolumeUp, MdSpeaker, MdVolumeMute } from 'react-icons/md';
import TodaysCompletedOrders from '../components/Orders/TodaysCompletedOrders';
function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const soundPlayedRef = useRef({});
  const audioRef = useRef(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const restaurantEmail = localStorage.getItem('user');
  const [, setRefresh] = useState(0); // dummy state to trigger rerender

  useEffect(() => {
    if (!restaurantEmail || typeof restaurantEmail !== 'string' || !restaurantEmail.includes('@')) {
      setErrorMsg('Invalid or missing restaurant email. Please log in again.');
      setLoading(false);
      return;
    }

    let unsubscribe;

    try {
      const q = query(
        collection(db, 'orders'),
        where('email', '==', restaurantEmail),
        where('delivered', '==', false),
        orderBy('createdDateTime', 'desc'),
      limit(50) // Only listen to latest 50 active orders
      );

      unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          const nowUTC = new Date();
          const activeOrders = [];
          const batch = writeBatch(db); // <-- initialize batch

          querySnapshot.forEach((docSnapshot) => {
            const data = docSnapshot.data();

            if (!data.createdDateTime || !data.order) return;

            let created;
            try {
              created = parseISO(data.createdDateTime);
            } catch (err) {
              console.warn('Invalid timestamp format:', data.createdDateTime);
              return;
            }

            const minutesSince = differenceInMinutes(nowUTC, created);

            if (minutesSince >= 30) {
              // Auto-complete orders older than 30 minutes
              const orderRef = doc(db, 'orders', docSnapshot.id);
              batch.update(orderRef, { delivered: 'autoCompleted' });
            } else {
              // Active recent orders
              activeOrders.push({ id: docSnapshot.id, ...data });

              if (userInteracted && !soundPlayedRef.current[docSnapshot.id]) {
                soundPlayedRef.current[docSnapshot.id] = true;
                audioRef.current?.play();
              }
            }
          });

          // Commit batch if there are any updates
          if (!batch._mutations || batch._mutations.length > 0) {
            try {
              await batch.commit();
            } catch (error) {
              console.error('Failed to auto-complete old orders:', error);
            }
          }

          setOrders(
            activeOrders.sort(
              (a, b) => new Date(a.createdDateTime) - new Date(b.createdDateTime)
            )
          );
          setLoading(false);
        },
        (error) => {
          console.error('Firestore listen failed:', error);
          setErrorMsg('Failed to fetch orders. Please try again later.');
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('Unexpected error setting up Firestore listener:', err);
      setErrorMsg('Unexpected error occurred.');
      setLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [restaurantEmail, userInteracted]);

  // Trigger rerender every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh((prev) => prev + 1);
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  const handleMarkDelivered = async (orderId) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        delivered: true,
      });
      setOrders((prevOrders) => prevOrders.filter((order) => order.id !== orderId));
    } catch (error) {
      console.error('Failed to mark order as delivered:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-gray-600">
        Loading orders...
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 text-red-500">
        {errorMsg}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Sticky Header */}
     <header className="sticky top-0 bg-white shadow-md z-10">
  <div className="max-w-6xl group mx-auto flex items-center justify-between px-4 py-3">
    <h1 className="text-xl font-bold text-[#6cbb23]">
      🍽️ Your Restaurant Orders
    </h1>

    {!userInteracted && (
      <button
        title="Volume"
        onClick={() => setUserInteracted(true)}
        className="bg-gray-400 hover:bg-green-600 text-white font-medium px-3 py-2 rounded-lg shadow"
      >
        <FaVolumeOff />
      </button>
    )}
    {userInteracted && (
      <button
        title="Volume"
        onClick={() => setUserInteracted(false)}
        className="bg-[#6cbb23] hover:bg-green-600 text-white font-medium px-3 py-2 rounded-lg shadow"
      >
        <FaVolumeUp />
      </button>
    )}
  </div>
</header>


    {/* Tabs Header */}
<div className="mt-4">
  {/* Mobile View: Centered label */}
  <div className="flex justify-center lg:hidden">
    <button className="text-[#6cbb23] border-b-2 border-[#6cbb23] font-semibold pb-2">
      Live Incoming Orders
    </button>
  </div>

  {/* Desktop View: 60/40 split with centered buttons */}
  <div className="hidden lg:flex">
    <div className="w-[100%] flex justify-center items-center">
      <button className="text-[#6cbb23] border-b-2 border-[#6cbb23] font-semibold pb-2">
        Live Incoming Orders
      </button>
    </div>
    {/* <div className="w-[40%] flex justify-center items-center">
      <button className="text-gray-700 font-semibold pb-2">
        Today’s Orders
      </button>
    </div> */}
  </div>
</div>



      {/* Orders List */}
     {/* Orders List */}
     
<div className="flex flex-col lg:flex-row">
  {/* 70% Orders Section */}
  <main className="lg:w-[100%] px-4 py-8">
    <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
      {orders.length === 0 ? (
        <div className="text-center text-gray-400 text-lg col-span-full">
          No recent orders.
        </div>
      ) : (
        orders.map((order) => {
          const created = parseISO(order.createdDateTime);
          const nowUTC = new Date(new Date().toISOString());
          const minutesLeft = 30 - differenceInMinutes(nowUTC, created);
          const percentRemaining = Math.max(0, (minutesLeft / 30) * 100);

          const [itemsRaw, tableRaw, cookingRaw, custphoneRaw, customerNameRaw] = order.order.split('|');
          const items = itemsRaw?.replace('Items:', '').trim();
          const tableNumber = tableRaw?.replace('Table:', '').trim();
          const cookingInstructions = cookingRaw?.replace('Cooking Instructions:', '').trim();
          const customerPhone = custphoneRaw?.replace('Phone Number:', '').trim();
          const customerName = customerNameRaw?.replace('Customer Name:', '').trim();

        const type = tableNumber?.toLowerCase().trim() || '';

let orderHeader = '';

if (!type || type.includes('delivery')) {
  orderHeader = '🚚 Delivery Order';
} else if (type.includes('take away')) {
  orderHeader = '🥡 Takeaway Order';
} else if (type.includes('room')) {
  orderHeader = '🛏️ Room Order';
} else {
  orderHeader = `🍽️ Order from Table ${tableNumber}`;
}

          let progressBarColor = 'from-green-400 to-green-600';
          if (minutesLeft <= 20 && minutesLeft > 15) {
            progressBarColor = 'from-orange-400 to-orange-600';
          } else if (minutesLeft <= 15) {
            progressBarColor = 'from-red-400 to-red-600';
          }

          return (
           <div
  key={order.id}
  className="relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-4 border border-gray-100 flex flex-col justify-between min-h-[260px]"
>
  <div
    className={`absolute top-0 left-0 h-1 bg-gradient-to-r ${progressBarColor} transition-all duration-500 ease-linear`}
    style={{ width: `${percentRemaining}%` }}
  ></div>

  <div className="text-sm font-semibold text-gray-700 mb-2">
    {orderHeader}
  </div>

  <div className="space-y-2 text-gray-800 mb-4">
    <div className="text-sm font-medium break-words">
      {items}
    </div>

    <div className="text-xs font-semibold text-indigo-600">
      🙋🏻‍♂️ {customerName}, {customerPhone}
    </div>

    {cookingInstructions && (
      <div className="text-xs text-gray-500">
        🍳 {cookingInstructions}
      </div>
    )}
    <div className="text-xs font-semibold text-indigo-600">
      {tableNumber && tableNumber.length <= 3 ? 'Table: ' : ''}
      {tableNumber}
    </div>
  </div>

  <div className="mt-auto">
    <div className="flex justify-between items-center text-xs text-gray-400 mb-3">
      <span>
        Placed {formatDistanceToNowStrict(created, { addSuffix: true })}
      </span>
      <span className="text-red-400">{minutesLeft} min left</span>
    </div>

    <button
      className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-full text-xs font-semibold transition-colors"
      onClick={() => handleMarkDelivered(order.id)}
    >
      Mark Delivered
    </button>
  </div>
</div>

          );
        })
      )}
    </div>
    {/* Show completed orders below cards on mobile */}
    <div className="lg:hidden mt-8">
      <TodaysCompletedOrders />
    </div>
  </main>

  {/* 30% Sidebar Section */}
 {/* <aside className="hidden lg:block lg:w-[40%] px-4 py-8">
    <TodaysCompletedOrders />
  </aside> */}
</div>


      <audio ref={audioRef} src={notificationSound} preload="auto" />
    </div>
  );
}

export default OrdersPage;
